import request from "@/utils/request.js";
import { FLAGS } from "@/config/featureFlags";
import { loadIfFeatureOn } from "@/utils/featureGates";

export const Reviews = () => {
  return request.get("/hotel/reviews/all");
};
// Get all reviews (paginated)
export const getReviewsaLLReview = (params) => {
  return request.get("/hotel/reviews/all", params);
};
// Login
export const goLogin = (data) => {
  return request.post("/auth/login", data);
};
// Dashboard overview (response rate, scores)
export const Dashboard = (data) => {
  return request.get("/hotel/dashboard", data);
};
export const getRestaurantDashboard = (data) => {
  return request.get("/restaurant/dashboard", data);
};
// Dashboard dynamic insights (categories, countries, travel types)
export const getDashboardDynamic = (params) => {
  return request.get("/hotel/dashboard/dynamic", params);
};
// The hotel's own analysis topics (WTK-913): tree with mentions and average score per topic.
// The reads below answer null (= the hotel has none) while FLAGS.WTK_913_CUSTOM_CATEGORIES is off
export const getCustomCategoryTree = (params) => {
  return loadIfFeatureOn(FLAGS.WTK_913_CUSTOM_CATEGORIES, () => request.get("/hotel/dashboard/custom-categories", params));
};
// The goal of each confirmed root topic, by topic id: { goals: { 16: 8.5, 22: null } } (WTK-988)
export const getCustomCategoryGoals = (params) => {
  return loadIfFeatureOn(FLAGS.WTK_913_CUSTOM_CATEGORIES, () => request.get("/hotel/dashboard/custom-categories/goals", params));
};
// data: { hotelId, goals }. A number sets a goal, null clears it, a topic left out keeps its goal
export const putCustomCategoryGoals = (data) => {
  return request.put("/hotel/dashboard/custom-categories/goals", data);
};
// Each category's mean score per period, on the period starts of /dynamic's timeline
export const getCustomCategoryTrend = (params) => {
  return loadIfFeatureOn(FLAGS.WTK_913_CUSTOM_CATEGORIES, () => request.get("/hotel/dashboard/custom-categories/trend", params));
};
// One page of the reviews mentioning a topic or a topic under it, with the quotes
export const getCustomCategoryMentions = (topicId, params) => {
  return loadIfFeatureOn(FLAGS.WTK_913_CUSTOM_CATEGORIES, () => request.get(`/hotel/dashboard/custom-categories/${topicId}/mentions`, params));
};
// The platform topic library (WTK-1019): { maxCategories, freePlaces, categories: [{ id, name, nameZh,
// covers, anchor, picked, topics }] }. freePlaces: how many more categories the hotel can add
export const getCustomCategoryLibrary = (params) => {
  return request.get("/hotel/dashboard/custom-categories/library", params);
};
// data: { hotelId, picked: [category id] }. The hotel's picks become exactly this list; one left out is
// unpicked and keeps its results. Returns the same shape as getCustomCategoryLibrary
export const putCustomCategoryLibrary = (data) => {
  return request.put("/hotel/dashboard/custom-categories/library", data);
};
// List hotels + restaurants for the current user (merges the former
// /hotel/dashboard/hotels and /restaurant/dashboard/restaurants endpoints).
// hotel/fetchHotelList, hotel/fetchRestaurantList and restaurant/fetchRestaurantList
// all call this within the same page load, so in-flight requests are deduped here —
// they share one request instead of firing 2-3 near-simultaneous calls.
// Cleared on failure (so a retry can happen) and on logout via clearAuthEntitiesCache.
let entitiesPromise = null;
export const getAuthEntities = () => {
  if (!entitiesPromise) {
    entitiesPromise = request.get("/auth/entities").catch((error) => {
      entitiesPromise = null;
      throw error;
    });
  }
  return entitiesPromise;
};
export const clearAuthEntitiesCache = () => {
  entitiesPromise = null;
};

// Authoritative category/subcategory taxonomy for a module — [{key, displayName,
// subCategories:[{key, displayName}]}]. entityType is "Hotel" or "Restaurant".
export const getServiceCategories = (entityType) => {
  return request.get("/common/service-categories", { entityType });
};
// Period report (weekly/monthly)
export const getDashboardPeriodReport = (params, config = {}) => {
  return request.get("/hotel/period-report", params, config);
};
// 半年报（本酒店）：periodType 固定 HALF_YEAR；periodStart 必须是已结束半年的 1 月 1 日或 7 月 1 日。
// 用 getStatus 拿到 HTTP 状态：200 = 已完成，202 = 还在生成（见 views/HalfYearReview/halfYearApi.js）
export const getHotelHalfYearReport = (params, config = {}) => {
  return request.getStatus("/hotel/period-report", { periodType: "HALF_YEAR", ...params }, config);
};
// 区间评论统计（半年报顶部的 Period Overview / Total Score 用）。
// 传 hotelId + startDate + endDate，一次拿回区间内的评论数、情感分布和每周均分，
// 不再按月发 6 个 /hotel/period-report 再自己拼。
export const getHotelReviewStatistics = (params, config = {}) => {
  return request.get("/hotel/period-report/review-statistics", params, config);
};
export const getDashboardPeriodReportRestaurant = (params, config = {}) => {
  return request.get("/restaurant/period-report", params, config);
};
// 同酒店下各餐厅（含自己）每月评论数（F&B 半年报 Reviews by month 用）。
// 传 restaurantId + startDate + endDate（"YYYY-MM-DD"），返回值没有 data 包一层，直接是数组：
// [{ restaurantId, restaurantName, monthlyCounts: [{ month: "2026-03", count }] }]
export const getSiblingMonthlyReviewCount = (params, config = {}) => {
  return request.get("/restaurant/period-report/sibling-monthly-review-count", params, config);
};
export const getDashboardPeriodReport1 = (params, config = {}) => {
  return request.getStatus("/hotel/period-report", params, config);
};
export const getDashboardPeriodReportRestaurant1 = (params, config = {}) => {
  return request.getStatus("/restaurant/period-report", params, config);
};
// Restaurant half-month period report (dynamic variant)
export const getRestaurantPeriodReportDynamic = (params, config = {}) => {
  return request.get("/restaurant/period-report/dynamic", params, config);
};
// Same as getDashboardPeriodReport but resolves the full response (status + data),
// so the caller can detect status 202 (report still generating) and poll.
export const getDashboardPeriodReportStatus = (params, config = {}) => {
  return request.getStatus("/hotel/period-report", params, config);
};
// Weekly category-score trend for the months before a given date (one call
// replaces looping /hotel/period-report per prior month)
export const getWeeklyCategoryTrend = (params, config = {}) => {
  return request.get("/hotel/period-report/weekly-category-trend", params, config);
};
// Export period report PDF
export const getPeriodReportPdf = (params, config = {}) => {
  return request.get("/hotel/period-report/report-pdf", params, { responseType: "blob", ...config });
};
// Hotel weekly competitor benchmark report (was /hotel/period-report?periodType=WEEK_BENCHMARK)
export const getHotelCompetitorsPeriodReport = (params, config = {}) => {
  return request.getStatus("/hotel/competitors/period-report", params, config);
};
// Restaurant competitor benchmark report (was /restaurant/dashboard/period-report?periodType=MONTH_COMPETITOR)
export const getRestaurantCompetitorsPeriodReport = (params, config = {}) => {
  return request.getStatus("/restaurant/competitors/period-report", params, config);
};
// 24h activity overview (risk signals + review summaries)
export const getActivityOverview = (params) => {
  return request.get("/hotel/dashboard/activity-overview", params);
};
export const getRestaurantActivityOverview = (params) => {
  return request.get("/restaurant/dashboard/activity-overview", params);
};
// Dashboard sentiment categories
export const reviewSentimentCategories = (data) => {
  return request.get("/dashboard/categories", data);
};



// List review sentiments (paginated with filters)
export const getSentimentReview = (params) => {
  return request.get("/hotel/sentiment", params);
};
// Homepage dashboard sentiment (returns all matching reviews in one shot, no pagination)
export const getDashboardSentiment = (params) => {
  return request.get("/hotel/dashboard/sentiment", params);
};
// Homepage dashboard top list (last week's top positives/negatives)
export const getDashboardTopList = (params) => {
  return request.get("/hotel/dashboard/top-list", params);
};
// Restaurant dashboard top list (last month's top positives/negatives)
export const getRestaurantDashboardTopList = (params) => {
  return request.get("/restaurant/dashboard/top-list", params);
};
// Count reviews by OTA source
export const getSentimentCount = (params) => {
  return request.get("/hotel/sentiment/count", params);
};
// Get single review detail
export const sentimentReview = (reviewId) => {
  return request.get(`/hotel/sentiment/${reviewId}`);
};
// Generate AI reply for a review
export const getSentimentReply = (params) => {
  return request.get("/hotel/sentiment/reply", params);
};
// Generate AI reply for a review
export const getRestaurantSentimentReply = (params) => {
  return request.get("/restaurant/sentiment/reply", params);
};
export const getTranslateText = (data) => {
  return request.post("/translate/text", data);
};
export const getTranslateByReview = (data) => {
  return request.post("/translate/by-review", data);
};

export const getReplyTemplates = (data) => {
  return request.get("/reply-templates", data);
};

// Hotel goal score settings
export const getGoal = (data) => {
  return request.get("/hotel/goal", data);
};
export const putGoal = (data) => {
  return request.put("/hotel/goal", data);
};

// Bookmarked reviews list
export const getHotelReplyStars = (params) => {
  return request.get("/hotel/reply-stars", params);
};
// Bookmark a review
export const pushHotelReplyStars = (data) => {
  return request.post("/hotel/reply-stars", data);
};
// Remove bookmark
export const delHotelReplyStars = (data) => {
  return request.delete(`/hotel/reply-stars/${data}`);
};

// Score-based reply templates (1-10)
export const getHotelReplyScoreTemplates = (params) => {
  return request.get("/hotel/reply-score-templates", params);
};
export const postHotelReplyScoreTemplates = (data) => {
  return request.post("/hotel/reply-score-templates", data);
};
// 餐厅：只打分没写文字的评论按分数（1–5）套用的回复模板，参数同酒店，hotelId 换成 restaurantId
export const getRestaurantReplyScoreTemplates = (params) => {
  return request.get("/restaurant/reply-score-templates", params);
};
export const postRestaurantReplyScoreTemplates = (data) => {
  return request.post("/restaurant/reply-score-templates", data);
};

// Restaurant goal score settings
export const getRestaurantGoal = (data) => {
  return request.get("/restaurant/goal", data);
};
export const putRestaurantGoal = (data) => {
  return request.put("/restaurant/goal", data);
};

// Restaurant reply stars
export const getRestaurantReplyStars = (params) => {
  return request.get("/restaurant/reply-stars", params);
};
export const pushRestaurantReplyStars = (data) => {
  return request.post("/restaurant/reply-stars", data);
};
export const delRestaurantReplyStars = (data) => {
  return request.delete(`/restaurant/reply-stars/${data}`);
};

// Score improvement: perfect reviews needed to raise each platform score by 0.1
export const getScoreImprovement = (params) => {
  return request.get("/hotel/dashboard/score-improvement", params);
};

export const getRestaurantScoreImprovement = (params) => {
  return request.get("/restaurant/dashboard/score-improvement", params);
};